import { useState, useEffect } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { formatHtml } from 'sanitize-text/sanitizers/HtmlSanitizer';
import VizExButton from 'visitor-ui-component-library/button/VizExButton';
import VizExLoadingButton from 'visitor-ui-component-library/button/VizExLoadingButton';
import { createThemeV2 } from 'visitor-ui-component-library/theme/createThemeV2';
import { OBSIDIAN, NEUTRAL_400, NEUTRAL_1600 } from 'visitor-ui-component-library/constants/WidgetColors';
import { EXTRA_EXTRA_SMALL, EXTRA_SMALL } from 'visitor-ui-component-library/constants/sizes';
import { getCanReadTextOnce } from 'visitor-ui-component-library/utils/colors';
import VizExSmall from 'visitor-ui-component-library/typography/VizExSmall';
import { useChatWidgetLocale } from '../ChatWidgetLocaleContext';
import { WELCOME_MESSAGE } from '../constants/consentPromptVariants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ConsentContainer = styled.div.withConfig({
  displayName: "ConsentPromptV2__ConsentContainer"
})(["display:flex;flex-direction:column;gap:8px;width:100%;position:relative;margin-top:", ";margin-bottom:", ";"], ({
  $variant,
  $isAIChatBot
}) => $variant === WELCOME_MESSAGE && !$isAIChatBot ? '16px' : '0', ({
  $variant,
  $isAIChatBot
}) => $variant !== WELCOME_MESSAGE && $isAIChatBot ? '8px' : '0');
const ConsentDivider = styled.hr.withConfig({
  displayName: "ConsentPromptV2__ConsentDivider"
})(["margin:0;width:100%;border:none;border-top:1px solid ", ";"], NEUTRAL_400);
const ConsentText = styled(VizExSmall).withConfig({
  displayName: "ConsentPromptV2__ConsentText"
})(["display:block;text-align:left;font-weight:300;font-size:12px;color:", ";line-height:", ";p{margin:0;}"], NEUTRAL_1600, ({
  $variant
}) => $variant === WELCOME_MESSAGE ? '18px' : '21px');
const BaseConsentButton = styled(VizExButton).withConfig({
  displayName: "ConsentPromptV2__BaseConsentButton"
})(["align-self:flex-start;font-weight:300;"]);
const WelcomeMessageButton = styled(BaseConsentButton).withConfig({
  displayName: "ConsentPromptV2__WelcomeMessageButton"
})(["height:24px;min-height:24px;padding:0 12px;font-size:12px;line-height:14px;"]);
const ThreadViewButton = styled(BaseConsentButton).withConfig({
  displayName: "ConsentPromptV2__ThreadViewButton"
})(["height:32px;min-height:32px;padding:0 16px;font-size:13px;line-height:18px;"]);
const SpamProtectionText = styled.div.withConfig({
  displayName: "ConsentPromptV2__SpamProtectionText"
})(["font-size:12px;line-height:18px;font-weight:500;color:", ";"], NEUTRAL_1600);
const ConsentPromptV2 = ({
  variant = WELCOME_MESSAGE,
  consentMessage,
  showConsentButton = false,
  onConsentAccept,
  showSpamProtection = false,
  accentColor,
  showDivider = true,
  isAIChatBot = false
}) => {
  const locale = useChatWidgetLocale();
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    setIsSubmitting(false);
  }, [consentMessage]);
  const handleConsentClick = event => {
    event.stopPropagation();
    if (onConsentAccept) {
      setIsSubmitting(true);
      onConsentAccept();
    }
  };
  const isTextReadable = accentColor ? getCanReadTextOnce(accentColor) : false;
  const primaryColor = isTextReadable ? accentColor : OBSIDIAN;
  const spinnerSize = variant === WELCOME_MESSAGE ? EXTRA_EXTRA_SMALL : EXTRA_SMALL;
  return /*#__PURE__*/_jsxs(ConsentContainer, {
    $variant: variant,
    $isAIChatBot: isAIChatBot,
    "data-test-id": "consent-prompt",
    children: [showDivider && /*#__PURE__*/_jsx(ConsentDivider, {}), consentMessage && /*#__PURE__*/_jsx(ConsentText, {
      $variant: variant,
      use: "help",
      "data-test-id": "consent-prompt-text",
      dangerouslySetInnerHTML: {
        __html: formatHtml(consentMessage)
      }
    }), showConsentButton && /*#__PURE__*/_jsx(VizExLoadingButton, {
      currentState: isSubmitting ? 'submitting' : 'ready',
      Button: variant === WELCOME_MESSAGE ? WelcomeMessageButton : ThreadViewButton,
      spinnerSize: spinnerSize,
      onClick: handleConsentClick,
      theme: createThemeV2({
        colors: {
          primary: primaryColor
        }
      }),
      "data-test-id": "consent-prompt-accept-button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "conversations-visitor-experience-components.buttons.agree",
        options: {
          locale
        }
      })
    }), showSpamProtection && /*#__PURE__*/_jsx(SpamProtectionText, {
      "data-test-id": "consent-prompt-spam-protection",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "conversations-visitor-experience-components.spamProtectionEnabled",
        options: {
          locale
        }
      })
    })]
  });
};
ConsentPromptV2.displayName = 'ConsentPromptV2';
export default ConsentPromptV2;